'use client'

import { useEffect, useRef, useState } from 'react'

import { confirm } from '@/core/lib/confirm'
import { toast } from '@/core/lib/toast'

import { useAppData } from '@/features/veluvit/hooks/use-app-data'
import { workFor } from '@/features/veluvit/lib/rules'
import { api, requestId } from '@/features/veluvit/services/api'
import { ensureAccount } from '@/features/veluvit/services/session.service'
import { useSessionStore } from '@/features/veluvit/stores/session.store'

/** 댓글 한 줄 — 스냅을 붙인 댓글은 검수를 거쳐 이미지·캡션만 공개된다(원본 대화는 공개하지 않는다) */
export type WorkComment = {
  id: string
  workId: string
  parentId: string | null
  author: { id: string; name: string }
  text: string
  snapshot: { image: string; caption: string } | null
  status: 'active' | 'pending' | 'rejected'
  likes: number
  liked: boolean
  createdAt: string
  /** 수정한 댓글 — 서버가 수정 시각을 주면 '수정됨'을 붙인다 */
  editedAt?: string | null
}

type Page = {
  comments: WorkComment[]
  total: number
  nextOffset: number | null
}

export const COMMENT_MAX = 2000

/** 보낼 때 덧붙이는 것 — 답글이면 parentId, 스냅이면 방 · 스냅 · 캡션 */
type CommentExtra = {
  parentId?: string
  snap?: { roomId: string; snapId: string; caption: string }
}

const errorText = (error: unknown, fallback: string) =>
  error instanceof Error && error.message ? error.message : fallback

/**
 * 작품 댓글 한 벌 — 목록(오래된 순, 50개씩) · 쓰기 · 답글(1단계) · 좋아요 · 수정 · 삭제(내 것) · 신고(남의 것).
 * 상세 화면 댓글 칸과 탐색(쇼츠) 댓글 패널이 같이 쓴다. enabled 가 false 면 불러오지 않는다(패널을 열 때 켠다).
 * 작가가 댓글 받기를 끈 작품(commentsOn: false)은 closed — 값이 없으면(예전 작품) 켜진 것으로 본다.
 */
export function useWorkComments(workId: string, enabled = true) {
  const { catalog, data } = useAppData()
  const identity = useSessionStore((state) => state.identity)
  const work = workFor(catalog, workId)
  const closed = work?.commentsOn === false
  const [items, setItems] = useState<WorkComment[] | null>(null)
  const [loadedScope, setLoadedScope] = useState<string | null>(null)
  const [total, setTotal] = useState(0)
  const [next, setNext] = useState<number | null>(null)
  const [failed, setFailed] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const morePending = useRef(false)
  const moreController = useRef<AbortController | null>(null)
  const scope = `${identity}:${workId}`
  const requestKey = closed || !enabled ? null : `${scope}:${attempt}`
  const [seenRequestKey, setSeenRequestKey] = useState(requestKey)
  if (seenRequestKey !== requestKey) {
    setSeenRequestKey(requestKey)
    if (requestKey) {
      setItems(null)
      setLoadedScope(null)
      setTotal(0)
      setNext(null)
      setFailed(false)
    }
  }

  useEffect(() => {
    if (closed || !enabled) return
    let alive = true
    const controller = new AbortController()
    moreController.current?.abort()
    morePending.current = false
    api<Page>(
      `/work/${workId}/comments/?offset=0`,
      undefined,
      'GET',
      controller.signal
    )
      .then((page) => {
        if (!alive || identity !== useSessionStore.getState().identity) return
        setItems(page.comments)
        setLoadedScope(scope)
        setTotal(page.total)
        setNext(page.nextOffset)
      })
      .catch(() => {
        if (
          alive &&
          !controller.signal.aborted &&
          identity === useSessionStore.getState().identity
        ) {
          setLoadedScope(scope)
          setFailed(true)
        }
      })
    return () => {
      alive = false
      controller.abort()
      moreController.current?.abort()
      morePending.current = false
    }
  }, [workId, closed, enabled, attempt, identity, scope])

  const retry = () => setAttempt((n) => n + 1)

  const loadMore = async () => {
    if (next === null || morePending.current) return
    const controller = new AbortController()
    moreController.current = controller
    morePending.current = true
    try {
      const page = await api<Page>(
        `/work/${workId}/comments/?offset=${next}`,
        undefined,
        'GET',
        controller.signal
      )
      if (
        controller.signal.aborted ||
        identity !== useSessionStore.getState().identity
      )
        return
      setItems((prev) => [...(prev || []), ...page.comments])
      setTotal(page.total)
      setNext(page.nextOffset)
    } catch {
      if (
        !controller.signal.aborted &&
        identity === useSessionStore.getState().identity
      )
        setFailed(true)
    } finally {
      if (moreController.current === controller) {
        moreController.current = null
        morePending.current = false
      }
    }
  }

  /** 보내기 — 성공하면 true(입력칸을 비우라는 뜻) */
  const post = async (text: string, extra: CommentExtra = {}) => {
    const body = text.trim()
    if (!body) return false
    try {
      await ensureAccount()
      const snap = extra.snap
        ? {
            roomId: extra.snap.roomId,
            snapId: extra.snap.snapId,
            shareSnapshot: true,
            caption: extra.snap.caption,
          }
        : {}
      const created = await api<WorkComment>(`/work/${workId}/comments/`, {
        requestId: requestId(),
        text: body,
        ...(extra.parentId ? { parentId: extra.parentId } : {}),
        ...snap,
      })
      if (created.status === 'pending')
        toast.show('스냅을 붙인 댓글은 검수를 마친 뒤 공개돼요', {
          duration: 3200,
        })
      else {
        setItems((prev) => [...(prev || []), created])
        setTotal((n) => n + 1)
      }
      return true
    } catch (error) {
      toast.show(errorText(error, '댓글을 보내지 못했어요.'), {
        duration: 3200,
      })
      return false
    }
  }

  const like = async (comment: WorkComment) => {
    // 누르자마자 바꾸고, 서버 응답으로 맞춘다. 실패하면 되돌린다
    const flip = (x: WorkComment) =>
      x.id === comment.id
        ? {
            ...x,
            liked: !comment.liked,
            likes: comment.likes + (comment.liked ? -1 : 1),
          }
        : x
    setItems((prev) => prev?.map(flip) || prev)
    try {
      await ensureAccount()
      const saved = await api<WorkComment>(
        `/work/${workId}/comment/${comment.id}/like/`,
        { liked: !comment.liked }
      )
      setItems(
        (prev) => prev?.map((x) => (x.id === saved.id ? saved : x)) || prev
      )
    } catch {
      setItems(
        (prev) => prev?.map((x) => (x.id === comment.id ? comment : x)) || prev
      )
    }
  }

  /** 내 댓글 고치기 — 성공하면 true */
  const edit = async (comment: WorkComment, text: string) => {
    const body = text.trim()
    if (!body || body === comment.text) return true
    try {
      const saved = await api<WorkComment>(
        `/work/${workId}/comment/${comment.id}/`,
        { text: body },
        'PATCH'
      )
      setItems(
        (prev) => prev?.map((x) => (x.id === saved.id ? saved : x)) || prev
      )
      return true
    } catch (error) {
      toast.show(errorText(error, '댓글을 고치지 못했어요.'), {
        duration: 3200,
      })
      return false
    }
  }

  /** 내 댓글 지우기 — 원댓글을 지우면 딸린 답글도 목록에서 함께 빠진다 */
  const remove = async (comment: WorkComment) => {
    if (
      !(await confirm('지운 댓글은 되돌릴 수 없어요.', {
        title: '댓글을 삭제할까요?',
        confirmLabel: '삭제',
        danger: true,
      }))
    )
      return
    try {
      await api(`/work/${workId}/comment/${comment.id}/`, undefined, 'DELETE')
      const gone = (x: WorkComment) =>
        x.id === comment.id || x.parentId === comment.id
      const removed = (items || []).filter(gone).length
      setItems((prev) => prev?.filter((x) => !gone(x)) || prev)
      setTotal((n) => Math.max(0, n - removed))
    } catch (error) {
      toast.show(errorText(error, '댓글을 지우지 못했어요.'), {
        duration: 3200,
      })
    }
  }

  const report = async (comment: WorkComment) => {
    if (
      !(await confirm('운영팀이 확인한 뒤 조치해요.', {
        title: '이 댓글을 신고할까요?',
        confirmLabel: '신고',
        danger: true,
      }))
    )
      return
    try {
      await ensureAccount()
      await api(`/work/${workId}/comment/${comment.id}/report/`, {
        text: '부적절한 댓글 신고',
      })
      toast.show('신고했어요')
    } catch (error) {
      toast.show(errorText(error, '신고하지 못했어요.'), { duration: 3200 })
    }
  }

  return {
    closed,
    items: loadedScope === scope ? items : null,
    total: loadedScope === scope ? total : 0,
    hasMore: loadedScope === scope && next !== null,
    failed: loadedScope === scope && failed,
    retry,
    creatorId: work?.creator?.id,
    mine: (x: WorkComment) => !!data?.user && x.author.id === data.user.id,
    loadMore,
    post,
    like,
    edit,
    remove,
    report,
  }
}

export type WorkComments = ReturnType<typeof useWorkComments>
